import { Link } from "react-router-dom";
import { formatBalance, formatAddress } from "../../utils/format";
import LoadingSpinner from "../loadingSpinner";
import { useState } from "react";

// Function to generate a readable contract name based on the address
function generateContractName(address, createdAtBlock) {
  if (!address) return "Unknown Contract";

  // Use the last 6 characters of the address to create a unique name
  const shortId = address.slice(-6);
  return `Contract ${shortId}`;
}

// Function to format the full address with copy functionality
function AddressDisplay({ address, type = "contract" }) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Link
        to={
          type === "contract" ? `/contracts/${address}` : `/accounts/${address}`
        }
        className="contract-address-link"
        title={address}
      >
        {formatAddress(address)}
      </Link>
      <button
        onClick={copyToClipboard}
        className="copy-button"
        title="Copy full address"
      >
        {copied ? "✓" : "⧉"}
      </button>
    </div>
  );
}

export default function ContractsList({
  contracts,
  loading,
  decimals,
  symbol,
}) {
  if (loading) {
    return <LoadingSpinner />;
  }

  if (!contracts || contracts.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "120px",
        }}
      >
        <p className="text-gray-500" style={{ textAlign: "center" }}>
          No contracts found
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="contracts-table">
        <thead>
          <tr>
            <th>Contract Address</th>
            <th>Name</th>
            <th>Creator</th>
            <th>Token Supply</th>
            <th>Created</th>
            <th>Transactions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {contracts.map((contract, index) => (
            <tr key={contract.address || index} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <AddressDisplay address={contract.address} type="contract" />
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {contract.name ||
                    generateContractName(
                      contract.address,
                      contract.createdAtBlock,
                    )}
                </div>
                {contract.symbol && (
                  <div className="text-sm text-gray-500">
                    Symbol: {contract.symbol}
                  </div>
                )}
                <div className="text-xs text-gray-400">
                  {contract.codeHash
                    ? `Code: ${formatAddress(contract.codeHash)}`
                    : "No code hash"}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <AddressDisplay address={contract.creator} type="account" />
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <span className="font-mono">
                  {contract.totalSupply || contract.total_supply || "-"}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                <div className="font-medium">
                  Block #{contract.createdAtBlock}
                </div>
                <div className="text-xs">
                  {new Date(contract.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </td>
              <td>
                <span className="transaction-badge">
                  {contract.transactionCount || 0}
                </span>
              </td>
              <td>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Link
                    to={`/contracts/${contract.address}`}
                    className="contract-address-link"
                  >
                    View
                  </Link>
                  <span style={{ color: "#d1d5db" }}>|</span>
                  <button
                    onClick={() =>
                      navigator.clipboard.writeText(contract.address)
                    }
                    className="copy-button"
                    style={{ color: "#6b7280" }}
                  >
                    Copy
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
